import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import styled from 'styled-components';
import { useAppSelector, useAppDispatch } from '@store/hooks';
import { openProjectModal } from '@store/slices/portfolioSlice';
const ProjectsSection = styled.section `
  position: relative;
  min-height: 220px;
  padding-top: 32px;
  z-index: 1;
  margin-bottom: 32px;
`;
const Title = styled.h2 `
  font-size: 1.6rem;
  font-weight: 700;
  margin-bottom: 16px;
`;
const Carousel = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 100%;
  margin: 0 auto 24px auto;
  max-width: 900px;
`;
const CarouselTrack = styled.div `
  overflow: hidden;
  width: 850px;
  max-width: 850px;
  position: relative;

  @media (max-width: 900px) {
    width: 100vw;
    max-width: 100vw;
  }
`;
const ProjectsContainer = styled.div `
  display: flex;
  flex-direction: row;
  transition: transform 0.7s cubic-bezier(0.4, 1, 0.6, 1);
  will-change: transform;
  gap: 18px;
  margin-bottom: 18px;
`;
const ProjectCard = styled.div `
  background: #fff;
  border: 1.5px solid #eee;
  border-radius: 14px;
  padding: 18px 16px 16px 16px;
  flex: 1;
  min-width: 320px;
  max-width: 340px;
  margin: 0 12px;
  flex-shrink: 0;
  position: relative;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.04);

  @media (max-width: 900px) {
    min-width: 80vw;
    max-width: 90vw;
  }
`;
const ProjectTitle = styled.h3 `
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0 0 8px 0;
`;
const ProjectDescription = styled.p `
  font-size: 0.98rem;
  margin: 0 0 8px 0;
`;
const ProjectTag = styled.span `
  font-size: 0.85rem;
  color: #ffce3c;
  font-weight: 600;
`;
const MoreDetailsLink = styled.a `
  position: absolute;
  right: 18px;
  bottom: 16px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #0a66c2;
  padding: 3px 18px;
  text-decoration: none;
  cursor: pointer;
  z-index: 2;
  transition: background 0.2s, color 0.2s, border 0.2s;
  letter-spacing: 0.01em;
  display: inline-block;

  &:hover {
    background: #0a66c2;
    color: #fff;
    border-radius: 999px;
  }
`;
const CarouselButton = styled.button `
  background: #fff;
  border: 1.5px solid #eee;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 1.5rem;
  color: #111;
  cursor: pointer;
  margin: 0;
  transition: background 0.2s, border 0.2s;
  z-index: 2;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);

  &.left {
    left: -24px;

    @media (max-width: 900px) {
      left: 0;
    }
  }

  &.right {
    right: -24px;

    @media (max-width: 900px) {
      right: 0;
    }
  }
`;
const ProjectIcon = styled.svg `
  position: absolute;
  right: 10px;
  top: 10px;
  width: 32px;
  height: 32px;
`;
const Projects = () => {
    const dispatch = useAppDispatch();
    const projects = useAppSelector((state) => state.portfolio.projects);
    const [currentIndex, setCurrentIndex] = React.useState(0);
    const handlePrevious = () => {
        setCurrentIndex(Math.max(0, currentIndex - 1));
    };
    const handleNext = () => {
        setCurrentIndex(Math.min(projects.length - 2, currentIndex + 1));
    };
    const handleMoreDetails = (projectId) => {
        dispatch(openProjectModal(projectId));
    };
    const getProjectIcon = (index) => {
        const icons = [
            _jsxs("g", { children: [_jsx("circle", { r: "16", cx: "16", cy: "16", fill: "#ffce3c" }), _jsx("path", { d: "M16 16 L16 0 A16 16 0 0 1 32 16 Z", fill: "#111" })] }, "icon1"),
            _jsxs("g", { children: [_jsx("rect", { x: "8", y: "8", width: "16", height: "16", fill: "#ffce3c" }), _jsx("rect", { x: "16", y: "8", width: "8", height: "16", fill: "#111" })] }, "icon2"),
            _jsxs("g", { children: [_jsx("rect", { x: "8", y: "16", width: "16", height: "8", fill: "#ffce3c" }), _jsx("rect", { x: "8", y: "8", width: "16", height: "8", fill: "#111" })] }, "icon3"),
            _jsxs("g", { children: [_jsx("circle", { r: "16", cx: "16", cy: "16", fill: "#ffce3c" }), _jsx("rect", { x: "8", y: "8", width: "16", height: "16", fill: "#111" })] }, "icon4"),
            _jsxs("g", { children: [_jsx("rect", { x: "8", y: "8", width: "16", height: "16", fill: "#ffce3c" }), _jsx("circle", { r: "8", cx: "16", cy: "16", fill: "#111" })] }, "icon5"),
        ];
        return icons[index % icons.length];
    };
    return (_jsxs(ProjectsSection, { className: "projects-section", children: [_jsx(Title, { children: "Projects" }), _jsxs(Carousel, { children: [_jsx(CarouselButton, { className: "left", onClick: handlePrevious, children: "\u2190" }), _jsx(CarouselTrack, { children: _jsx(ProjectsContainer, { style: {
                                transform: `translateX(${-currentIndex * (320 + 24)}px)` // card width + margin
                            }, children: projects.map((project, index) => (_jsxs(ProjectCard, { children: [_jsx(ProjectIcon, { width: "32", height: "32", viewBox: "0 0 32 32", children: getProjectIcon(index) }), _jsx(ProjectTitle, { children: project.title }), _jsx(ProjectDescription, { children: project.description }), _jsx(ProjectTag, { children: project.tag }), _jsx(MoreDetailsLink, { href: "#", onClick: (e) => {
                                            e.preventDefault();
                                            handleMoreDetails(project.id);
                                        }, children: "More Details" })] }, project.id))) }) }), _jsx(CarouselButton, { className: "right", onClick: handleNext, children: "\u2192" })] })] }));
};
export default Projects;
